import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

@Component({
  selector: 'app-passcode-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    MessageModule,
  ],
  template: `
    <p-dialog
      [visible]="visible"
      [modal]="true"
      [closable]="false"
      [draggable]="false"
      [resizable]="false"
      header="Enter Passcode"
      [style]="{ width: '400px' }"
    >
      <div class="flex flex-column gap-3">
        <p-message
          *ngIf="error"
          severity="error"
          text="Invalid passcode. Please try again."
        ></p-message>

        <div class="text-center">
          <label for="passcode" class="block text-900 font-medium mb-2">
            Enter 6-digit passcode:
          </label>
          <input
            id="passcode"
            pInputText
            [(ngModel)]="passcode"
            (input)="onPasscodeChange($event)"
            (keydown)="onKeyDown($event)"
            placeholder="000000"
            maxlength="6"
            class="w-full text-center text-2xl tracking-widest"
            [disabled]="loading"
          />
        </div>

        <div class="flex justify-content-center gap-2 mt-3">
          <p-button
            label="Cancel"
            (onClick)="onCancel()"
            [disabled]="loading"
            severity="secondary"
          ></p-button>
          <p-button
            label="Submit"
            (onClick)="onSubmit()"
            [loading]="loading"
            [disabled]="passcode.length !== 6"
          ></p-button>
        </div>
      </div>
    </p-dialog>
  `,
  styles: [
    `
      input[type='text'] {
        letter-spacing: 0.5em;
      }
    `,
  ],
})
export class PasscodeDialogComponent {
  @Output() passcodeSubmitted = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  visible = false;
  passcode = '';
  loading = false;
  error = false;

  show() {
    this.visible = true;
    this.passcode = '';
    this.error = false;
    this.loading = false;
  }

  hide() {
    this.visible = false;
    this.passcode = '';
    this.error = false;
    this.loading = false;
  }

  onPasscodeChange(event: any) {
    // Only allow digits
    const value = event.target.value.replace(/\D/g, '');
    this.passcode = value;
    this.error = false;
  }

  onKeyDown(event: KeyboardEvent) {
    // Allow backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true)
    ) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if (
      (event.shiftKey || event.keyCode < 48 || event.keyCode > 57) &&
      (event.keyCode < 96 || event.keyCode > 105)
    ) {
      event.preventDefault();
    }
  }

  onSubmit() {
    if (this.passcode.length === 6) {
      this.loading = true;
      this.error = false;
      this.passcodeSubmitted.emit(this.passcode);
      // Don't hide immediately - let the parent component control when to hide
    }
  }

  onCancel() {
    this.cancelled.emit();
    this.hide();
  }

  setError() {
    this.error = true;
    this.loading = false;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }
}
